import {computed, inject, Injectable} from '@angular/core';
import {toObservable} from '@angular/core/rxjs-interop';
import {UserGroup} from '@core/api/data-access';
import {EntitySignalStore, useIdbPersistence} from '@feature-signal-store';
import {ToastService} from '@utils';
import {filter, map, Observable, of} from 'rxjs';
import {useDevtools} from 'signalstory';
import {IUserGroupsQueryMarkers} from '../../data';
import {UsersService} from '../../services/users.service';
import {IQueryUserGroupsResponse, IStateUserGroup, IUserGroupsGlobalState, userGroupsGlobalInitialState} from './user-groups.state';

@Injectable({
	providedIn: 'root',
})
export class UserGroupsStore extends EntitySignalStore<IStateUserGroup, IUserGroupsGlobalState> {
	/* ------------ Providers / Services ------------ */
	public readonly usersService: UsersService = inject(UsersService);
	public readonly toastService: ToastService = inject(ToastService);
	/* ---------------- Properties ---------------- */
	/* ---------------- Computed ---------------- */
	public readonly userGroupsMap$ = computed<Map<string, IStateUserGroup>>(() => {
		return Object.values(this.state().Entities.entityMap).reduce((acc, entityUserGroup) => {
			if (!entityUserGroup || !entityUserGroup.id) return acc;
			acc.set(entityUserGroup.id, entityUserGroup);
			return acc;
		}, new Map<string, IStateUserGroup>());
	});

	public readonly userGroupsArray$ = computed<IStateUserGroup[]>(() => {
		return Object.values(this.state().Entities.entityMap);
	});

	public readonly userGroupsArrayObservable$ = toObservable(this.userGroupsArray$);

	/* ---------------- Constructor ---------------- */
	constructor() {
		super(
			'User Groups',
			'Global',
			'id',
			{
				isEntityStore: true,
				plugins: [useDevtools(), useIdbPersistence()],
			},
			userGroupsGlobalInitialState,
		);
	}

	/* -------------------------------- State Methods -------------------------------- */
	private ensureLoaded(): Observable<void> {
		if (this.state().loaded || this.state().loading) return of(null);
		return this.loadGroups().pipe(
			map(() => {
				this.update(state => ({...state, loaded: true}));
			}),
			filter(() => this.state().loaded),
		);
	}
	// Local querying, returns observable with the relevant groups
	public queryUserGroups(queryMarkers: IUserGroupsQueryMarkers, page: number, size: number): Observable<IQueryUserGroupsResponse> {
		const {showLocked, sortBy, sortDesc, searchText} = queryMarkers;
		return this.ensureLoaded()
			.pipe(
				filter(() => this.state().loaded),
				map(() => this.userGroupsArray$()),
			)
			.pipe(
				map(groups => {
					// Filter by locked status if needed
					let filteredGroups = showLocked !== undefined && showLocked !== null ? groups.filter(group => group.group?.locked === showLocked) : groups;

					// Sort by the specified field
					if (sortBy) {
						filteredGroups = [...filteredGroups].sort((a, b) => {
							const valueA = a.group?.[sortBy];
							const valueB = b.group?.[sortBy];
							const compareResult = valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
							return sortDesc ? -compareResult : compareResult;
						});
					}

					// Calculate pagination
					const totalGroups = filteredGroups.length;
					// If size is Infinity, return all results without pagination
					const isInfinite = size === Infinity || size < 0;
					const validPage = isNaN(page) || isInfinite ? 1 : page;
					const fromGroup = isInfinite ? 0 : (validPage - 1) * size;
					const toGroup = isInfinite ? totalGroups : Math.min(fromGroup + size, totalGroups);

					// Extract paginated groups
					const paginatedGroups = filteredGroups.slice(fromGroup, toGroup);

					// Apply search filter if searchText is provided
					const searchFilteredGroups = searchText ? paginatedGroups.filter(group => group.group.name.toLowerCase().includes(searchText.toLowerCase())) : paginatedGroups;

					// Map to the required response format
					return {
						totalGroups,
						fromGroup,
						toGroup,
						groups: searchFilteredGroups.map(group => group.group),
					};
				}),
			);
	}

	/* -------------------------------- User Groups Methods -------------------------------- */
	/**
	 * טעינת כל קבוצות המשתמשים
	 */
	public loadGroups(): Observable<UserGroup[]> {
		this.update(state => ({...state, loading: true}));
		return this.usersService.getGroups().pipe(
			map(response => {
				const groups = response.groups;
				const groupsToStore: IStateUserGroup[] = groups.map(group => this.createStateUserGroup(group.id, {backendGroup: group}));
				this.addEntities(groupsToStore);
				this.update(state => ({...state, loaded: true, loading: false}));
				return groupsToStore.map(group => group.group);
			}),
		);
	}

	/**
	 * טעינת קבוצת משתמשים בודדת
	 * @param groupId מזהה הקבוצה
	 */
	public loadGroup(groupId: string): Observable<UserGroup> {
		return this.usersService.getGroup(groupId).pipe(
			map(response => {
				const group = response.group;
				this.addEntity(this.createStateUserGroup(group.id, {backendGroup: group}));
				return group;
			}),
		);
	}

	/**
	 * יצירת קבוצת משתמשים חדשה
	 * @param group נתוני הקבוצה
	 */
	public createGroup(group: UserGroup): Observable<UserGroup> {
		return this.usersService.createGroup(group).pipe(
			map(response => {
				const newGroup = this.createStateUserGroup(response.group.id, {backendGroup: response.group});
				this.addEntity(newGroup);
				return newGroup.group;
			}),
		);
	}

	/**
	 * עדכון קבוצת משתמשים קיימת
	 * @param group נתוני הקבוצה לעדכון
	 */
	public updateGroup(group: UserGroup): Observable<UserGroup> {
		return this.usersService.updateGroup(group).pipe(
			map(response => {
				const updatedGroup = response.group;
				const groupIndex = this.state().Entities.entityMap[updatedGroup.id];
				if (groupIndex) {
					groupIndex.group = updatedGroup;
				}
				this.updateEntity(updatedGroup.id, entity => {
					return {
						...entity,
						group: new UserGroup(updatedGroup),
					};
				});
				return updatedGroup;
			}),
		);
	}

	/**
	 * מחיקת קבוצת משתמשים
	 * @param groupId מזהה הקבוצה למחיקה
	 */
	public deleteGroup(groupId: string): Observable<any> {
		return this.usersService.deleteGroup(groupId).pipe(
			map(() => {
				this.removeEntity(groupId);
			}),
		);
	}

	public assignUserToGroup(userId: string, groupId: string): Observable<UserGroup> {
		const group = this.state().Entities.entityMap[groupId];
		if (!group) return of(null);
		const updatedUsers = new Set(group.group.users);
		updatedUsers.add(userId);
		group.group.users = Array.from(updatedUsers);
		return this.updateGroup(group.group);
	}

	public duplicateGroup(id: string) {
		const group = this.state().Entities.entityMap[id];
		if (!group) return of(null);
		const newGroup = new UserGroup(group.group);
		newGroup.id = '';
		newGroup.name = newGroup.name + ` (${new Date().toLocaleString()})`;
		return this.createGroup(newGroup);
	}

	/* -------------------------------- Private Methods -------------------------------- */
	private createStateUserGroup(id: string, data: {backendGroup?: UserGroup}, override = false): IStateUserGroup {
		if (!id) return null;
		let {backendGroup} = data;
		backendGroup = backendGroup instanceof UserGroup ? backendGroup : new UserGroup(backendGroup);
		let userState: IStateUserGroup = this.userGroupsMap$()?.get(id);
		if (!userState) {
			userState = {
				id,
				group: backendGroup,
				loadingState: 'success',
			};
		}
		userState.group = backendGroup;
		userState.loadingState = 'success';
		return userState;
	}
}
